import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ isCollapsed = false, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isPlatformExpanded, setIsPlatformExpanded] = useState(false);
  const { signOut } = useAuth();

  // Dental-practice-specific items shown at top level
  const dentalMenuItems = [
    { name: 'Dashboard', path: '/dashboard', icon: 'Home' },
    { name: 'Patient Management', path: '/patient-management', icon: 'Users' },
    { name: 'Appointments', path: '/appointment-management', icon: 'Calendar' },
    { name: 'Clinical Notes', path: '/clinical-notes', icon: 'FileText' },
    { name: 'Perio Chart', path: '/perio-chart', icon: 'Activity' },
    { name: 'Communications', path: '/communications', icon: 'MessageSquare' },
    { name: 'Patient Feedback', path: '/patient-feedback', icon: 'MessageSquare' },
    { name: 'Patient Portal', path: '/patient-portal', icon: 'Users' },
    { name: 'Insurance Eligibility', path: '/insurance-eligibility', icon: 'Shield' },
    { name: 'Eligibility Re-Verify', path: '/eligibility-reverification-scheduler', icon: 'CalendarClock' },
    { name: 'Insurance Management', path: '/insurance-management', icon: 'ClipboardList' },
    { name: 'Claims Processing', path: '/claims-processing', icon: 'DollarSign' },
    { name: 'Claims Helper', path: '/claims-helper', icon: 'ClipboardList' },
    { name: 'Denied Claims', path: '/denied-claims-management', icon: 'AlertTriangle' },
    { name: 'Revenue Analytics', path: '/revenue-analytics', icon: 'BarChart3' },
    { name: 'Patient Outcomes', path: '/patient-outcomes', icon: 'Heart' },
    { name: 'Radiology Gallery', path: '/radiology-gallery-center', icon: 'Images' },
    { name: 'Practice Role Management', path: '/practice-role-management', icon: 'Network' },
    { name: 'Practice Setup', path: '/practice-setup', icon: 'Settings2' },
    { name: 'Data Migration Tool', path: '/data-migration-tool', icon: 'DatabaseZap' },
  ];

  // Technology/platform items grouped under a single collapsible section
  const platformMenuItems = [
    { name: 'All Pages', path: '/all-pages', icon: 'LayoutGrid' },
    { name: 'AI Claims Analysis', path: '/ai-claims-analysis', icon: 'Sparkles' },
    { name: 'AI Revenue Forecasting', path: '/ai-revenue-forecasting', icon: 'BarChart3' },
    { name: 'Practice Benchmarking', path: '/practice-benchmarking-dashboard', icon: 'BarChart3' },
    { name: 'AI Agent Orchestrator', path: '/ai-agent-orchestrator', icon: 'Activity' },
    { name: 'AI Action Panel', path: '/ai-action-panel', icon: 'Sparkles' },
    { name: 'Agentic Workflow Automation', path: '/agentic-workflow-automation', icon: 'Sparkles' },
    { name: 'Agentic AI Guardrails', path: '/agentic-guardrails', icon: 'ShieldAlert' },
    { name: 'RAG Knowledge Admin', path: '/rag-admin-ingestion', icon: 'Database' },
    { name: 'RAG Analytics', path: '/rag-analytics-dashboard', icon: 'BarChart3' },
    { name: 'AI Cost Dashboard', path: '/ai-cost-dashboard', icon: 'DollarSign' },
    { name: 'Prompt & Retrieval Lab', path: '/prompt-retrieval-lab', icon: 'FlaskConical' },
    { name: 'HIPAA Audit Center', path: '/hipaa-audit-center', icon: 'Shield' },
    { name: 'System Health Monitor', path: '/system-health-monitor', icon: 'Activity' },
    { name: 'Live Activity Dashboard', path: '/live-activity-dashboard', icon: 'Activity' },
    { name: 'Unified Observability', path: '/unified-observability-dashboard', icon: 'BarChart3' },
    { name: 'Observability Integrations', path: '/observability-integrations', icon: 'Activity' },
    { name: 'Deployment Center', path: '/deployment-center', icon: 'Rocket' },
    { name: 'Monitoring Dashboard', path: '/monitoring-dashboard', icon: 'ShieldCheck' },
    { name: 'Load Test Dashboard', path: '/load-test-dashboard', icon: 'Gauge' },
    { name: 'Integration Management', path: '/integration-management', icon: 'Settings' },
    { name: 'SMS Notifications', path: '/twilio-sms-notifications', icon: 'MessageSquare' },
    { name: 'Onboarding & Training', path: '/onboarding-training', icon: 'GraduationCap' },
    { name: 'Marketing Deck', path: '/marketing-presentation', icon: 'Presentation' },
    { name: 'Technical Docs', path: '/technical-documentation-center', icon: 'FileText' },
    { name: 'Patient Detail', path: '/patient-detail', icon: 'User' },
    { name: 'Appointment Detail', path: '/appointment-detail', icon: 'CalendarCheck' },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileOpen(false);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  // Auto-expand platform section if current path is in platform items
  useEffect(() => {
    const isPlatformPath = platformMenuItems?.some(item => item?.path === location?.pathname);
    if (isPlatformPath) {
      setIsPlatformExpanded(true);
    }
  }, [location?.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileOpen]);

  const isPlatformActive = platformMenuItems?.some(item => item?.path === location?.pathname);

  return (
    <>
      <button
        onClick={toggleMobileMenu}
        className="mobile-menu-button"
        aria-label="Toggle mobile menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={20} />
      </button>
      <div
        className={`mobile-overlay ${!isMobileOpen ? 'hidden' : ''}`}
        onClick={() => setIsMobileOpen(false)}
      />
      <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'translate-x-0' : ''}`}>
        <div className="sidebar-header">
          {!isCollapsed && (
            <>
              <div className="sidebar-logo">
                <img
                  src="/assets/images/ora_fusion_ai_logo.png"
                  alt="ORA FUSION AI logo - Modern dental platform with gradient design"
                  className="rounded-xl object-contain"
                  style={{ width: '75px', height: '75px' }}
                />
              </div>
              <span className="sidebar-brand">ORA FUSION AI</span>
            </>
          )}
          {onToggle && (
            <button
              onClick={onToggle}
              className={`sidebar-collapse-btn ${isCollapsed ? 'mx-auto' : 'ml-auto'}`}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={16} />
            </button>
          )}
        </div>

        <nav className="sidebar-nav sidebar-nav-scroll">
          {/* Dental-specific items */}
          {dentalMenuItems?.map((item) => {
            const isActive = location?.pathname === item?.path;
            return (
              <div
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
              >
                <Icon name={item?.icon} size={20} />
                <span className="sidebar-nav-label">{item?.name}</span>
                {item?.badge && (
                  <span className="sidebar-nav-badge">{item?.badge}</span>
                )}
              </div>
            );
          })}

          {/* Platform & Technology collapsible group */}
          <div
            onClick={() => !isCollapsed && setIsPlatformExpanded(!isPlatformExpanded)}
            className={`sidebar-nav-item ${isPlatformActive ? 'active' : ''}`}
            style={{ cursor: 'pointer' }}
            title={isCollapsed ? 'Platform & Technology' : undefined}
          >
            <Icon name="Cpu" size={20} />
            <span className="sidebar-nav-label" style={{ flex: 1 }}>Platform & Technology</span>
            {!isCollapsed && (
              <Icon
                name={isPlatformExpanded ? 'ChevronUp' : 'ChevronDown'}
                size={14}
                style={{ opacity: 0.6, flexShrink: 0 }}
              />
            )}
          </div>

          {/* Platform sub-items — shown when expanded and sidebar not collapsed */}
          {isPlatformExpanded && !isCollapsed && platformMenuItems?.map((item) => {
            const isActive = location?.pathname === item?.path;
            return (
              <div
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                style={{ paddingLeft: '2.25rem' }}
              >
                <Icon name={item?.icon} size={16} />
                <span className="sidebar-nav-label" style={{ fontSize: '0.8rem' }}>{item?.name}</span>
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer" style={{ flexShrink: 0 }}>
          <div 
            onClick={handleLogout}
            className="sidebar-nav-item logout-item"
          >
            <Icon name="LogOut" size={20} />
            <span className="sidebar-nav-label">Logout</span>
          </div>
        </div>
      </aside>
      <div className="mobile-bottom-nav">
        <div className="mobile-bottom-nav-content">
          {dentalMenuItems?.slice(0, 3)?.map((item) => {
            const isActive = location?.pathname === item?.path;
            return (
              <div
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`mobile-nav-item ${isActive ? 'active' : ''}`}
              >
                <div className="relative">
                  <Icon name={item?.icon} size={20} />
                  {item?.badge && (
                    <span className="mobile-nav-badge">{item?.badge}</span>
                  )}
                </div>
                <span className="mobile-nav-label">{item?.name}</span>
              </div>
            );
          })}
          <div
            onClick={() => navigate('/practice-setup')}
            className={`mobile-nav-item ${location?.pathname === '/practice-setup' ? 'active' : ''}`}
          >
            <Icon name="Settings" size={20} />
            <span className="mobile-nav-label">Settings</span>
          </div>
          <div
            onClick={handleLogout}
            className="mobile-nav-item logout-mobile"
          >
            <Icon name="LogOut" size={20} />
            <span className="mobile-nav-label">Logout</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;